import React from 'react';
import { Route, Routes } from 'react-router-dom';
import Submissions from '../pages/Submissions/Submissions';
import Audits from '../pages/Audits/Audits';
import BroadcastAddressBook from '../pages/ChangeBroadcast/AddressBook/BroadcastAddressBook';
import BroadcastConfirmation from '../pages/ChangeBroadcast/BroadcastConfirmation/BroadcastConfirmation';
import Drafts from '../pages/Drafts/Drafts';
import { SubmissionProduct } from '../pages/Product/SubmissionProduct';
import CreateSubmission from '../pages/CreateSubmission/CreateSubmission';
import { DocumentType } from '@common/types';
import { paths, routes } from './routes';
import { globalRoutes } from '@common/routing/routes';
import PublishedProductGroup from '../pages/ProductGroup/PublishedProductGroup';
import PublishedProduct from '../pages/Product/PublishedProduct';
import { SubmissionContentDetails } from '../pages/ContentDetails/SubmissionContentDetails';
import { ViewHtmlCustomer, ViewHtmlCustomerPublished } from '../pages/ViewHtmlCustomer/ViewHtmlCustomer';
import PublishedProductsList from '../pages/Products/PublishedProductsList';
import { SubmissionProductGroup } from '../pages/ProductGroup/SubmissionProductGroup';
import PublishedContentDetails from '../pages/ContentDetails/PublishedContentDetails';
import { SubmissionTaskContentDetails } from '../pages/ContentDetails/SubmissionTaskContentDetails';
import { SubmissionTaskProduct } from '../pages/Product/SubmissionTaskProduct';
import { TaskDetailsWrapper } from '../pages/TaskDetails/TaskDetailsWrapper';
import Dashboard from '../pages/Dashboard/Dashboard';
import { withAuthenticationRequired } from 'react-oidc-context';
import { PrintPage } from '@common/pages/ViewHtml/PrintPage';
import { PrintPublishedDocument } from '@common/pages/ViewHtml/PrintPublishedDocument';
import { useFeatureFlags } from '@common/hooks/useFeatureFlags';
import { SubmissionTaskContentDetailsReplacement } from '../pages/ContentDetails/SubmissionTaskContentDetailsReplacement';
import { ContentDetailsMode } from '../pages/ContentDetails/types';

const documentTypesToRender = [
  DocumentType.Pil,
  DocumentType.Smpc,
  DocumentType.Rmm,
  DocumentType.Dhcp,
  DocumentType.UserManual,
  DocumentType.SafetyAlert,
  DocumentType.ProductInformation,
  DocumentType.AudioVideo,
  DocumentType.Video,
  DocumentType.Audio,
];

const DefaultRoutes = () => {
  const { enableSendBroadcast } = useFeatureFlags();
  return (
    <Routes>
      <Route path={globalRoutes.dashboard} element={<Dashboard />} />
      <Route path={routes.products} element={<PublishedProductsList />} />
      <Route path={routes.submissions} element={<Submissions />} />
      <Route path={routes.audits} element={<Audits />} />
      <Route path={routes.drafts} element={<Drafts />} />
      <Route path={routes.createSubmission} element={<CreateSubmission />} />
      <Route path={`${routes.submissions}/:submissionId/${paths.submissionTasks}/:submissionTaskId`} element={<TaskDetailsWrapper />}/>
      <Route path={`${routes.submissions}/:submissionId`} element={<SubmissionProductGroup />} />
      {documentTypesToRender.map((contentType) => (
        <Route key={contentType} path={`${routes.submissions}/:submissionId/${contentType.toLowerCase()}/:documentId`} element={<SubmissionContentDetails contentType={contentType} printUrl={`/${paths.document}/:documentId/:documentType/:version/published/${paths.print}`} />}/>
      ))}
      {documentTypesToRender.map((contentType) => (
        <Route key={contentType} path={`${routes.submissions}/:submissionId/${contentType.toLowerCase()}/:documentId/view`}
          element={<SubmissionContentDetails contentType={contentType} printUrl={`/${paths.document}/:documentId/:documentType/:version/published/${paths.print}`} readonly />} />
      ))}
      {documentTypesToRender.map((contentType) => (
        <Route key={contentType} path={`${routes.submissions}/:submissionId/${contentType.toLowerCase()}/:documentId/update`}
          element={<SubmissionContentDetails contentType={contentType} printUrl={`/${paths.document}/:documentId/:documentType/:version/published/${paths.print}`} />}/>
      ))}
      {documentTypesToRender.map((contentType) => (
        <Route key={contentType}
          path={`${routes.submissions}/:submissionId/${paths.submissionTasks}/:taskId/${contentType.toLowerCase()}/:documentId/update`}
          element={<SubmissionTaskContentDetails contentType={contentType} printUrl={`/${paths.document}/:documentId/:documentType/:version/published/${paths.print}`} mode={ContentDetailsMode.Update} />} />
      ))}
      {documentTypesToRender.map((contentType) => (
        <Route key={contentType}
          path={`${routes.submissions}/:submissionId/${paths.submissionTasks}/:taskId/${contentType.toLowerCase()}/:documentId/replacement-update`}
          element={<SubmissionTaskContentDetailsReplacement contentType={contentType} printUrl={`/${paths.document}/:documentId/:documentType/:version/published/${paths.print}`}/>} />
      ))}
      {documentTypesToRender.map((contentType) => (
        <Route key={contentType} path={`${routes.productGroups}/:productGroupId/${contentType.toLowerCase()}/:documentId`}
          element={<PublishedContentDetails contentType={contentType} printUrl={`/${paths.document}/:documentId/:documentType/:version/published/${paths.print}`}/>} />
      ))}
      <Route path={`${routes.productGroups}/:productGroupId${routes.product}/:productId`} element={<PublishedProduct />} />
      <Route path={`${routes.submissions}/:submissionId${routes.product}/:productId`} element={<SubmissionProduct />} />
      <Route path={`${routes.submissions}/:submissionId${routes.product}/:productId/view`} element={<SubmissionProduct readonly />}/>
      <Route path={`${routes.submissions}/:submissionId/${paths.submissionTasks}/:taskId${routes.product}/:productId`}
        element={<SubmissionTaskProduct />} />
      <Route path={`${routes.submissions}/:submissionId/${paths.submissionTasks}/:taskId${routes.product}/:productId/view`}
        element={<SubmissionTaskProduct readonly />} />
      <Route path={`${routes.productGroups}/:productGroupId`} element={<PublishedProductGroup />} />

      <Route path={`${routes.submissions}/:submissionId/${paths.submissionTasks}/:taskId/${paths.html}`} 
        element={<ViewHtmlCustomer printUrl={`/${paths.submissionTasks}/:taskId/${paths.print}`} />}/>
      
      <Route path={`${routes.submissions}/:submissionId/${paths.submissionTasks}/:taskId/published`}
        element={<ViewHtmlCustomerPublished printUrl={`/${paths.document}/:documentId/:documentType/:version/published/${paths.print}`} />}
      />
      <Route path={`${paths.submissionTasks}/:taskId/${paths.print}`} element={<PrintPage />} />

      <Route path={`${paths.document}/:documentId/:documentType/:version/published/${paths.print}`} element={
        <PrintPublishedDocument />
      } />
      
      {enableSendBroadcast && (
        <>
          <Route
            path={`${routes.changeBroadcast}/:taskId`}
            element={<BroadcastAddressBook />}
          />
          <Route
            path={`${routes.changeBroadcast}/:taskId/${paths.changeBroadcastSend}`}
            element={<BroadcastConfirmation />}
          />
        </>
      )}
  </Routes>
  );
};

export default withAuthenticationRequired(DefaultRoutes);
